/**
 * Die Karte „Auftrag an Vespera“ im Chat. (18.10.2026, Paket P1, shared/vespera-auftraege.ts, server/vespera-auftraege.ts)
 *
 * Zeigt GENAU den Inhalt des Auftrags (Auftraggeber, Ziel, Dienst, Domains, Werkzeuge, Schritte, erwartetes Ergebnis, Zeitlimit,
 * Kennung und Kurzwert des Hashs) und, solange der Vorschlag offen ist, die Knöpfe „Ja, ausführen“ / „Nein“.
 *
 * ⚠️ Der Knopf ist Martins Hand: erst sein Klick ruft den Server. Der Server prüft Sitzung, Kanal, Hash und Frist — die Karte
 *    entscheidet nichts. Sie schickt den Hash mit, den sie GEZEIGT hat. In P1 bedeutet Ja nur „freigegeben“; ausgeführt wird nichts.
 * ⚠️ Doppelklick-sicher: der erste Klick sperrt beide Knöpfe sofort; scheitert der Aufruf, gehen sie wieder auf und der Fehler steht da.
 * ⚠️ Alle Texte nur über textContent. Barrierefreiheit: Gruppe mit Beschriftung, Status als `role="status"`, Knöpfe 44 Punkte hoch.
 */
import { entwickler } from "../../shared/entwickler.js";
import type { Auftrag } from "../../shared/vespera-auftraege.js";

export const KARTEN_KLASSE = "auftrag-karte";
export const KARTEN_HOST_KLASSE = "auftrag-karten";
/** Pflichtkopf der Martin-Wege (server/vespera-auftraege-routen.ts KNOPF_KOPF). */
export const KNOPF_KOPF = "X-Vespera-Knopf";
/** So lange nach dem Zeichnen der Karte bleibt „Ja, ausführen“ gesperrt (Lesezeit; jedes Neuzeichnen beginnt sie neu). */
export const JA_SPERRE_MS = 2500;
/** So viele Karten höchstens auf einmal; offene zuerst, dann die jüngsten. */
export const KARTEN_HOECHSTENS = 3;
/** Erledigte Karten bleiben so lange sichtbar (ms). */
export const ERLEDIGT_SICHTBAR_MS = 30 * 60_000;

export type AuftragEntscheid = "ja" | "nein";
/** Sendet Martins Entscheid an den Server; `true` = der Server hat angenommen. */
export type AuftragSender = (id: string, entscheid: AuftragEntscheid, hash: string) => Promise<boolean> | boolean;

const STATUS_TEXT: Record<Auftrag["status"], string> = {
  vorgeschlagen: "wartet auf dein Ja",
  freigegeben: "freigegeben (noch nichts ausgeführt)",
  abgelehnt: "abgelehnt",
  abgelaufen: "abgelaufen",
};

/** Der Standard-Sender: ruft die Martin-Wege mit Sitzung und Pflichtkopf. */
export const sendeAuftragEntscheid: AuftragSender = async (id, entscheid, hash) => {
  try {
    const r = await fetch(`/api/vespera-auftrag/${encodeURIComponent(id)}/${entscheid}`, {
      method: "POST",
      credentials: "include",
      headers: { [KNOPF_KOPF]: "1", "Content-Type": "application/json" },
      body: JSON.stringify(entscheid === "ja" ? { hash } : {}),
    });
    return r.ok;
  } catch {
    return false;
  }
};

function zeile(doc: Document, titel: string, inhalt: string | readonly string[], liste = false): HTMLElement[] {
  const dt = doc.createElement("dt");
  dt.className = "auftrag-feld";
  dt.textContent = titel;
  const dd = doc.createElement("dd");
  dd.className = "auftrag-wert";
  if (typeof inhalt === "string") dd.textContent = inhalt;
  else {
    const l = doc.createElement(liste ? "ol" : "ul");
    l.className = "auftrag-liste";
    for (const t of inhalt) {
      const li = doc.createElement("li");
      li.textContent = t;
      l.appendChild(li);
    }
    dd.appendChild(l);
  }
  return [dt, dd];
}

/** Eine Karte (noch nicht eingehängt). */
export function baueAuftragsKarte(doc: Document, a: Auftrag, sende: AuftragSender, jetzt: () => number = Date.now): HTMLElement {
  const wer = entwickler(a.auftraggeber).name;
  const karte = doc.createElement("section");
  karte.className = KARTEN_KLASSE;
  karte.dataset.id = a.id;
  karte.dataset.status = a.status;
  karte.setAttribute("aria-label", `Auftrag an Vespera von ${wer}, ${STATUS_TEXT[a.status]}`);

  const kopf = doc.createElement("h3");
  kopf.className = "auftrag-titel";
  kopf.textContent = `${wer} bittet Vespera um einen Auftrag`;
  karte.appendChild(kopf);

  const felder = doc.createElement("dl");
  felder.className = "auftrag-felder";
  felder.append(
    ...zeile(doc, "Ziel", a.ziel),
    ...zeile(doc, "Dienst", a.dienst),
    ...zeile(doc, "Erlaubte Seiten", a.domains),
    ...zeile(doc, "Erlaubte Werkzeuge", a.werkzeuge),
    ...zeile(doc, "Schritte", a.schritte, true),
    ...zeile(doc, "Erwartetes Ergebnis", a.erwartet),
    ...zeile(doc, "Zeitlimit", `${a.zeitlimitMin} Minuten`),
    ...zeile(doc, "Kennung", `${a.id} · Prüfwert ${a.hash.slice(0, 12)}`),
  );
  karte.appendChild(felder);

  const status = doc.createElement("p");
  status.className = "auftrag-status";
  status.setAttribute("role", "status");
  status.textContent = STATUS_TEXT[a.status];
  karte.appendChild(status);

  if (a.status === "vorgeschlagen") {
    const knoepfe = doc.createElement("div");
    knoepfe.className = "auftrag-knoepfe";
    knoepfe.setAttribute("role", "group");
    knoepfe.setAttribute("aria-label", "Entscheidung zum Auftrag");
    const bereitAb = jetzt() + JA_SPERRE_MS;
    const mach = (text: string, entscheid: AuftragEntscheid): HTMLButtonElement => {
      const k = doc.createElement("button");
      k.type = "button";
      k.className = `auftrag-knopf auftrag-knopf-${entscheid}`;
      k.textContent = text;
      k.addEventListener("click", (ev) => {
        if (!ev.isTrusted) return; // künstliches Ereignis (Skript, Fremdtext): ignorieren
        if (entscheid === "ja" && jetzt() < bereitAb) return; // eben erst erschienen: das Ja ist noch nicht scharf
        if (karte.dataset.sendet === "1") return;
        karte.dataset.sendet = "1";
        for (const b of knoepfe.querySelectorAll("button")) b.disabled = true;
        status.textContent = "wird gesendet …";
        void Promise.resolve(sende(a.id, entscheid, a.hash)).then(
          (ok) => {
            if (ok) return; // der Server schickt den neuen Stand über den Strom
            oeffneWieder();
          },
          () => oeffneWieder(),
        );
      });
      return k;
    };
    const oeffneWieder = (): void => {
      delete karte.dataset.sendet;
      for (const b of knoepfe.querySelectorAll("button")) b.disabled = b.classList.contains("auftrag-knopf-ja") && jetzt() < bereitAb;
      status.textContent = "Das hat nicht geklappt — der Auftrag gilt vielleicht nicht mehr. Versuch es noch einmal.";
    };
    const jaKnopf = mach("Ja, ausführen", "ja");
    jaKnopf.disabled = true;
    // Scharf nach der Lesezeit — außer es wird gerade gesendet. Der Klick prüft die Zeit zusätzlich selbst.
    setTimeout(() => {
      if (karte.dataset.sendet !== "1") jaKnopf.disabled = false;
    }, JA_SPERRE_MS);
    knoepfe.append(jaKnopf, mach("Nein", "nein"));
    karte.appendChild(knoepfe);
  }
  return karte;
}

/** Welche Karten stehen jetzt da: offene zuerst, dann kürzlich erledigte; höchstens KARTEN_HOECHSTENS. Rein. */
export function sichtbareAuftraege(liste: readonly Auftrag[], jetzt: number): Auftrag[] {
  const offen = liste.filter((a) => a.status === "vorgeschlagen");
  const erledigt = liste.filter((a) => a.status !== "vorgeschlagen" && jetzt - a.geaendertMs <= ERLEDIGT_SICHTBAR_MS).sort((x, y) => y.geaendertMs - x.geaendertMs);
  return [...offen, ...erledigt].slice(0, KARTEN_HOECHSTENS);
}

/** Hält den Stand und zeichnet die Karten am Ende des Verlaufs neu (idempotent). */
export function baueAuftragsAnzeige(wurzel: HTMLElement, sende: AuftragSender = sendeAuftragEntscheid, jetzt: () => number = Date.now): { setze(liste: readonly Auftrag[]): void } {
  const doc = wurzel.ownerDocument;
  let host: HTMLElement | null = null;
  return {
    setze(liste) {
      const karten = sichtbareAuftraege(liste, jetzt());
      if (karten.length === 0) {
        host?.remove();
        host = null;
        return;
      }
      if (!host) {
        host = doc.createElement("div");
        host.className = KARTEN_HOST_KLASSE;
      }
      host.replaceChildren(...karten.map((a) => baueAuftragsKarte(doc, a, sende, jetzt)));
      if (wurzel.lastElementChild !== host) wurzel.appendChild(host);
    },
  };
}
